import type { ComponentType, ReactNode } from 'react';
import { MessageCircle, Plus, RefreshCw, Search, Users, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AutomationsWorkspaceShell,
  type AutomationsNetworkTab,
} from '@/components/automation/AutomationsWorkspaceShell';
import {
  BoardFilterDropdown,
  type BoardFilterOption,
} from '@/components/crm/BoardFilterDropdown';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/lib/api/services/teamService';

export type TeamManagementTab = 'members' | 'messaging' | 'round-robin';

export const TEAM_ROLE_FILTER_OPTIONS: BoardFilterOption[] = [
  { id: 'ADMIN', label: 'Admin', color: '#2E90FA' },
  { id: 'AGENT', label: 'Agent', color: '#F79009' },
  { id: 'USER', label: 'User', color: '#98A2B3' },
  { id: 'RIDER', label: 'Rider', color: '#F79009' },
];

export const TEAM_STATUS_FILTER_OPTIONS: BoardFilterOption[] = [
  { id: 'active', label: 'Active', color: '#12B76A' },
  { id: 'pending', label: 'Pending invite', color: '#F79009' },
  { id: 'inactive', label: 'Inactive', color: '#98A2B3' },
];

export const TEAM_ROTATION_FILTER_OPTIONS: BoardFilterOption[] = [
  { id: 'in', label: 'In rotation', color: '#12B76A' },
  { id: 'out', label: 'Not in rotation', color: '#98A2B3' },
];

export function teamMemberInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';
}

export function teamMemberStatusKey(
  member: TeamMember,
  invitationStatus: { status: string } | null,
): 'active' | 'pending' | 'inactive' {
  if (member.is_active === false || member.status === 'inactive') return 'inactive';
  if (invitationStatus?.status === 'pending' || invitationStatus?.status === 'never-logged-in') {
    return 'pending';
  }
  return 'active';
}

export function formatBusinessAccessLabel(
  member: TeamMember,
  accounts: Array<{ id: string; name: string }>,
  allAccountCount: number,
): string {
  if (
    member.is_organization_owner
    || member.is_super_admin
    || (member.role || '').toString().toUpperCase() === 'ADMIN'
  ) {
    if (accounts.length === 0 || (allAccountCount > 0 && accounts.length >= allAccountCount)) {
      return 'All accounts';
    }
  }
  if (accounts.length === 0) return '—';
  if (accounts.length === 1) return accounts[0].name;
  return `${accounts[0].name} +${accounts.length - 1}`;
}

type RolePillTone = { label: string; className: string };

export function teamRolePillFromRole(role: string | null | undefined): RolePillTone {
  const normalized = (role || '').toString().toUpperCase();
  if (normalized === 'ADMIN') return { label: 'Admin', className: 'bg-[#EFF8FF] text-[#175CD3]' };
  if (normalized === 'AGENT') return { label: 'Agent', className: 'bg-[#FFFAEB] text-[#B54708]' };
  if (normalized === 'RIDER') return { label: 'Rider', className: 'bg-[#FFFAEB] text-[#B54708]' };
  if (normalized === 'USER') return { label: 'User', className: 'bg-[#F2F4F7] text-[#344054]' };
  return { label: normalized || 'User', className: 'bg-[#F2F4F7] text-[#344054]' };
}

export function teamRolePill(member: TeamMember): RolePillTone {
  if (member.is_organization_owner) {
    return { label: 'Owner', className: 'bg-[#E7F7EC] text-[#076A2D]' };
  }
  return teamRolePillFromRole(member.role);
}

type DotPillTone = { label: string; className: string; dot: string };

export function teamStatusPill(
  statusKey: 'active' | 'pending' | 'inactive',
): DotPillTone {
  if (statusKey === 'pending') {
    return {
      label: 'Pending invite',
      className: 'bg-[#FFFAEB] text-[#B54708]',
      dot: 'bg-[#F79009]',
    };
  }
  if (statusKey === 'inactive') {
    return {
      label: 'Inactive',
      className: 'bg-[#F2F4F7] text-[#475467]',
      dot: 'bg-[#98A2B3]',
    };
  }
  return {
    label: 'Active',
    className: 'bg-[#E7F7EC] text-[#076A2D]',
    dot: 'bg-[#12B76A]',
  };
}

export function DotStatusPill({
  label,
  className,
  dot,
}: {
  label: string;
  className: string;
  dot: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-medium',
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dot)} aria-hidden />
      {label}
    </span>
  );
}

export function SoftRolePill({ label, className }: RolePillTone) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-semibold',
        className,
      )}
    >
      {label}
    </span>
  );
}

const TAB_META: Record<
  TeamManagementTab,
  { label: string; Icon: ComponentType<{ className?: string }> }
> = {
  members: { label: 'Members', Icon: Users },
  messaging: { label: 'Message', Icon: MessageCircle },
  'round-robin': { label: 'Round Robin', Icon: RefreshCw },
};

type TeamShellProps = {
  tab: TeamManagementTab;
  onTabChange: (tab: TeamManagementTab) => void;
  memberCount: number;
  filterBar?: ReactNode;
  children: ReactNode;
};

/** Card-Theme shell — same underline tabs as Automations / Social / CRM. */
export function TeamManagementShell({
  tab,
  onTabChange,
  memberCount,
  filterBar,
  children,
}: TeamShellProps) {
  const tabs: AutomationsNetworkTab<TeamManagementTab>[] = [
    {
      id: 'members',
      label: TAB_META.members.label,
      Icon: TAB_META.members.Icon,
      count: memberCount,
      show: true,
    },
    {
      id: 'messaging',
      label: TAB_META.messaging.label,
      Icon: TAB_META.messaging.Icon,
      show: true,
    },
    {
      id: 'round-robin',
      label: TAB_META['round-robin'].label,
      Icon: TAB_META['round-robin'].Icon,
      show: true,
    },
  ];

  return (
    <AutomationsWorkspaceShell
      network={tab}
      tabs={tabs}
      onNetworkChange={onTabChange}
      filterBar={filterBar}
    >
      {children}
    </AutomationsWorkspaceShell>
  );
}

type MembersFilterBarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  roleSelected: string[];
  onRoleChange: (next: string[]) => void;
  statusSelected: string[];
  onStatusChange: (next: string[]) => void;
  onInvite?: () => void;
  inviteDisabled?: boolean;
  inviteHint?: string | null;
};

/** Same visual row as AutomationsWorkspaceFilterBar / PostsWorkspaceFilterBar. */
export function TeamMembersFilterBar({
  search,
  onSearchChange,
  roleSelected,
  onRoleChange,
  statusSelected,
  onStatusChange,
  onInvite,
  inviteDisabled = false,
  inviteHint = null,
}: MembersFilterBarProps) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-[#EAECF0] bg-white px-[15px] py-3">
        <div className="flex h-9 w-full max-w-[280px] items-center gap-2 rounded-lg border border-[#E4E7EC] bg-white px-2.5 sm:w-[260px]">
          <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search in view…"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-[#101828] outline-none placeholder:text-[#98A2B3]"
          />
          {search ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onSearchChange('')}
              className="text-[#98A2B3] hover:text-[#101828]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>

        <BoardFilterDropdown
          label="Role"
          mode="single"
          showCheckbox={false}
          searchable={false}
          selected={roleSelected}
          onChange={onRoleChange}
          options={TEAM_ROLE_FILTER_OPTIONS}
        />
        <BoardFilterDropdown
          label="Status"
          mode="single"
          showCheckbox={false}
          searchable={false}
          selected={statusSelected}
          onChange={onStatusChange}
          options={TEAM_STATUS_FILTER_OPTIONS}
        />

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {onInvite ? (
            <button
              type="button"
              onClick={onInvite}
              disabled={inviteDisabled}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#0EB64F] px-3 text-[13px] font-semibold text-white transition hover:bg-[#0A8A3B] disabled:pointer-events-none disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Invite teammate
            </button>
          ) : null}
        </div>
      </div>
      {inviteHint ? (
        <p className="border-b border-[#EAECF0] bg-[#FFFAEB] px-[15px] py-2 text-[12px] text-[#B54708]">
          {inviteHint}
        </p>
      ) : null}
    </div>
  );
}

type RoundRobinFilterBarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  rotationSelected: string[];
  onRotationChange: (next: string[]) => void;
  statusSelected: string[];
  onStatusChange: (next: string[]) => void;
  autoAssignEnabled: boolean;
  autoAssignLoading?: boolean;
  autoAssignPending?: boolean;
  onAutoAssignChange: (enabled: boolean) => void;
};

/** Same chrome as TeamMembersFilterBar — search + filters + Auto-assign on the right. */
export function TeamRoundRobinFilterBar({
  search,
  onSearchChange,
  rotationSelected,
  onRotationChange,
  statusSelected,
  onStatusChange,
  autoAssignEnabled,
  autoAssignLoading = false,
  autoAssignPending = false,
  onAutoAssignChange,
}: RoundRobinFilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#EAECF0] bg-white px-[15px] py-3">
      <div className="flex h-9 w-full max-w-[280px] items-center gap-2 rounded-lg border border-[#E4E7EC] bg-white px-2.5 sm:w-[260px]">
        <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search in view…"
          className="min-w-0 flex-1 bg-transparent text-[13px] text-[#101828] outline-none placeholder:text-[#98A2B3]"
        />
        {search ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onSearchChange('')}
            className="text-[#98A2B3] hover:text-[#101828]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      <BoardFilterDropdown
        label="Rotation"
        mode="single"
        showCheckbox={false}
        searchable={false}
        selected={rotationSelected}
        onChange={onRotationChange}
        options={TEAM_ROTATION_FILTER_OPTIONS}
      />
      <BoardFilterDropdown
        label="Status"
        mode="single"
        showCheckbox={false}
        searchable={false}
        selected={statusSelected}
        onChange={onStatusChange}
        options={TEAM_STATUS_FILTER_OPTIONS.filter((opt) => opt.id !== 'pending')}
      />

      <div className="ml-auto flex items-center gap-3">
        <div className="hidden text-right sm:block">
          <p className="text-[13px] font-semibold text-[#101828]">Auto-assign</p>
          <p className="text-[11px] text-[#667085]">Rotate new WhatsApp chats</p>
        </div>
        {autoAssignLoading ? (
          <Skeleton className="h-6 w-11 rounded-full" />
        ) : (
          <Switch
            checked={autoAssignEnabled}
            onCheckedChange={onAutoAssignChange}
            disabled={autoAssignPending}
            aria-label="Auto-assign round robin"
          />
        )}
      </div>
    </div>
  );
}
